/**
 * App-owned pronunciation dictionary library (spec 2026-07-29).
 * Listing/resolution goes through the backend — the browser never calls
 * ElevenLabs dictionary-management endpoints for library dictionaries.
 */
import { apiClient } from '@/services/apiClient'
import type { PronunciationDictionaryLocator } from '@/services/pronunciationDictionary'

export interface LibraryDictionary {
  id: string
  name: string
  description?: string | null
  rules_count: number
  has_phonemes: boolean
  updated_at: string
}

export interface ResolveResult {
  locators: PronunciationDictionaryLocator[]
  skipped: { id: string; reason: string }[]
}

const BASE = '/api/pronunciation-dictionaries'

export function listPronunciationDictionaries(): Promise<LibraryDictionary[]> {
  return apiClient.get<LibraryDictionary[]>(BASE)
}

export function resolveLibraryLocators(ids: string[]): Promise<ResolveResult> {
  return apiClient.post<ResolveResult>(`${BASE}/resolve-locators`, {
    dictionary_ids: ids,
  })
}

/** Persona-rules locator always first; ElevenLabs caps a request at 3. */
export function composePronunciationLocators(
  rulesLocator: PronunciationDictionaryLocator | undefined,
  libraryLocators: PronunciationDictionaryLocator[],
): PronunciationDictionaryLocator[] {
  const combined = rulesLocator ? [rulesLocator, ...libraryLocators] : [...libraryLocators]
  return combined.slice(0, 3)
}

async function multipart(
  path: string,
  method: 'POST' | 'PUT',
  input: { name?: string; description?: string; file?: File },
): Promise<LibraryDictionary> {
  const form = new FormData()
  if (input.name !== undefined) form.set('name', input.name)
  if (input.description !== undefined) form.set('description', input.description)
  if (input.file) form.set('file', input.file)
  const res = await apiClient.fetch(path, { method, body: form })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`${method} ${path} failed (${res.status}): ${text}`)
  }
  return res.json()
}

export function uploadPronunciationDictionary(input: {
  name: string
  description?: string
  file: File
}): Promise<LibraryDictionary> {
  return multipart(BASE, 'POST', input)
}

export function updatePronunciationDictionary(
  id: string,
  input: { name?: string; description?: string; file?: File },
): Promise<LibraryDictionary> {
  return multipart(`${BASE}/${id}`, 'PUT', input)
}

export async function archivePronunciationDictionary(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`)
}
